import React, { useContext } from 'react';
import { type StackProps, type TextProps, View, XStack, YStack } from 'tamagui';

import { isEmpty } from '../../misc';
import { Icon, type IconProps, type IconType } from '../icons/Icon';
import { BodyMediumRegular, BodySmallRegular } from '../text/Typography';
import { RowListHasHeaderContext } from './RowList';

export interface BaseRowProps {
    title: string;
    subtitle?: string;
    bottomContent?: React.ReactNode;
    leftIcon?: IconType;
    leftContentOverride?: React.ReactElement;
    containerProps?: StackProps;
    titleProps?: TextProps;
    subtitleProps?: TextProps;
    iconProps?: Omit<IconProps, 'icon'>;
    onPress?: () => void;
}

export interface RowProps extends BaseRowProps {
    rightContent?: React.ReactNode;
}

/**
 * Base Row component - flexible and generic.
 * Not meant to be used often. Prefer specific row variants like CheckboxRow, NavigationRow, etc.
 */
export const Row = ({
    title,
    subtitle,
    bottomContent,
    leftIcon,
    leftContentOverride,
    rightContent,
    onPress,
    containerProps,
    titleProps,
    subtitleProps,
    iconProps,
}: RowProps) => {
    const isInteractive = onPress != null;
    const hasHeader = useContext(RowListHasHeaderContext);
    const backgroundColor = hasHeader ? '$gray100' : '$gray200';
    const hoverPressBackgroundColor = hasHeader ? '$gray200' : '$gray300';

    // Resolve leftContent: use leftContentOverride, or icon, or null
    const leftContent =
        leftContentOverride ??
        (leftIcon != null ? (
            <Icon color="$black" icon={leftIcon} {...iconProps} />
        ) : null);

    return (
        <XStack
            width="100%"
            backgroundColor={backgroundColor}
            alignItems="center"
            justifyContent="space-between"
            gap={12}
            paddingVertical={12}
            paddingHorizontal={16}
            onPress={onPress}
            cursor={isInteractive ? 'pointer' : 'default'}
            hoverStyle={
                isInteractive
                    ? {
                          backgroundColor: hoverPressBackgroundColor,
                      }
                    : undefined
            }
            pressStyle={
                isInteractive
                    ? {
                          backgroundColor: hoverPressBackgroundColor,
                      }
                    : undefined
            }
            {...containerProps}
        >
            {leftContent != null && <View flexShrink={0}>{leftContent}</View>}
            <YStack flex={1} minHeight={32} justifyContent="center">
                <BodyMediumRegular {...titleProps}>{title}</BodyMediumRegular>
                {!isEmpty(subtitle) && (
                    <BodySmallRegular color="$gray1000" {...subtitleProps}>
                        {subtitle}
                    </BodySmallRegular>
                )}
                {bottomContent}
            </YStack>
            {rightContent != null && <View flexShrink={0}>{rightContent}</View>}
        </XStack>
    );
};
